import { useEffect, useRef, useState } from "react";

/* ─── COPY BANK ─────────────────────────────────────────────────────────── */

const PT_BR = {
  pageTitle: "Lexiun — O Especialista Jurídico de IA. Em Cada Caso.",
  nav: {
    signIn: "Entrar",
  },
  hero: {
    headline: "Lexiun trabalha para você.\nPensa, age, entrega.",
    subline:
      "Lexiun se torna o especialista dedicado em cada caso do seu escritório. Constrói o caso a partir de qualquer material. Conhece cada detalhe. Age antes de você pedir.\nLê, redige, monitora, verifica e responde — onde você estiver.\nSempre ligado. Também no WhatsApp.",
    sublineClose: "Não apenas uma ferramenta de IA. Um Especialista. Em cada caso.",
    cta: "Acesso Antecipado",
    badge: "Preview privado · Lançamento Q3 2026",
  },
  problem: {
    quote: "IA pode inventar jurisprudência que parece real.",
    body: "O maior risco da IA jurídica não é dar uma resposta errada — é dar uma resposta errada convincente. Lexiun elimina esse risco. Um exército de agentes especializados, alimentados por múltiplos modelos de IA de fronteira, inspecionam e analisam cruzadamente o trabalho uns dos outros. Cada resultado é verificado e fundamentado nas fontes do próprio caso. Nenhuma jurisprudência fabricada. Nenhum caso inventado. Cada citação é real e rastreável.",
  },
  answer: {
    headline: "Mais que responder perguntas. Lexiun se torna o especialista de cada um dos seus casos.",
    body: "Do primeiro rascunho à última decisão, Lexiun constrói conhecimento permanente sobre o caso. Ele lê tudo. Não esquece nada. Detecta o que humanos deixam passar. E fica mais preciso a cada caso.",
    body2: "Lexiun é o especialista 24/7 sempre disponível — pronto para responder qualquer consulta com precisão cirúrgica, debater uma ideia de última hora, ou agir a partir de uma mensagem de voz no WhatsApp do advogado à meia-noite.",
  },
  capabilities: {
    label: "O que o seu especialista faz",
    cards: [
      { title: "Memória Total", body: "Nenhum detalhe perdido. Nenhum documento ignorado. Memória completa do caso que nunca degrada, nunca reseta, nunca esquece." },
      { title: "Radar de Riscos", body: "Falhas de citação. Prazos perdidos. Problemas de legitimidade. Riscos prescricionais. Detectados automaticamente, sinalizados instantaneamente." },
      { title: "Petições Completas", body: "Da estratégia jurídica à petição formatada com jurisprudência citada, anexos estruturados e apêndices completos. Em minutos." },
      { title: "Reescreve em Segundos", body: "Destaque um parágrafo. Descreva o que quer. O documento inteiro se ajusta — argumentos, citações, estrutura. Segundos." },
      { title: "Toda Citação É Real", body: "Cada citação, cada referência — verificada e fundamentada em fontes reais. Nenhuma jurisprudência inventada. Nunca." },
      { title: "Trabalha Enquanto Você Dorme", body: "Enquanto você dorme, Lexiun monitora prazos, sinaliza novos riscos e prepara materiais. Pronto quando você estiver." },
      { title: "Seu Parceiro de Raciocínio", body: "Peça ao Lexiun para encontrar falhas no seu argumento. Ele vai. E depois vai te ajudar a construir um melhor." },
      { title: "Do Caos ao Caso", body: "Áudios de WhatsApp, emails, conversas com clientes, documentos, fotos — jogue tudo. Lexiun constrói o caso a partir do caos bruto. Minutos, não meses." },
    ],
  },
  workflow: {
    headline: "Pare de redigir. Concentre-se em decidir.",
    steps: ["Descreva\no que precisa\nem linguagem\ncomum", "Lexiun\nredige, formata,\ncita e anexa", "Revise\ne aprove\nou refine com\numa frase"],
    detail: "Expresse sua intenção em linguagem simples — por chat, voz ou WhatsApp. Lexiun faz o resto.",
    closing: "Estratégia é o trabalho do advogado. Todo o resto é do Lexiun.",
  },
  chaosToCase: {
    headline: "Despeje tudo. Lexiun constrói o caso.",
    body: "Advogados não recebem informações em pastas organizadas. Recebem mensagens de voz à meia-noite, cadeias de email com 47 encaminhamentos, documentos escaneados em ordem aleatória e anotações em guardanapos. Lexiun absorve tudo, dá sentido a cada peça e constrói o caso do zero.",
    inputs: ["Áudios de voz", "Emails", "Documentos", "Conversas", "Fotos", "Processos"],
    outputs: ["Fatos", "Partes", "Legislação", "Análise", "Caso pronto"],
  },
  midCta: {
    line: "Chega de ler. Vamos conversar.",
    cta: "Acesso Antecipado",
  },
  firm: {
    headline: "Um especialista em cada caso. Em todo o escritório.",
    orgLabel: "ORGANIZAÇÃO",
    nodeLabels: {
      lawyerA: "Advogado A + IA",
      lawyerB: "Advogado B + IA",
      clientPortal: "Portal do Cliente",
    },
    points: [
      "Cada advogado recebe um especialista de IA dedicado — em todos os seus casos",
      "Padrões de qualidade do escritório, aplicados automaticamente",
      "Clientes podem acompanhar o status dos seus próprios casos — com acesso configurável",
      "Controles organizacionais, permissões por caso",
    ],
  },
  whatsapp: {
    label: "MENSAGEIRO INSTANTÂNEO · CANAL DE CONVENIÊNCIA",
    headline: "Seu assistente. No WhatsApp. Sempre.",
    subhead: "Acesse seu agente Lexiun a qualquer hora. Texto, voz, documentos. E deixe Lexiun falar com as pessoas em seu nome.",
    panels: [
      {
        tag: "↔ FALE COM SEU ASSISTENTE",
        subtitle: "Acesse Lexiun de onde estiver. Voz ou texto. A qualquer hora.",
        items: [
          { message: "\"Redija uma réplica para Andrade vs. Silva, com foco na prescrição quinquenal.\"", caption: "Envie instruções por voz de qualquer lugar. Lexiun entrega em minutos." },
          { message: "[encaminhado: áudio do cliente, contrato escaneado, foto de anotações]", caption: "Encaminhe qualquer coisa. Lexiun captura e ingere no caso." },
          { message: "\"Qual o status do caso Andrade? Me leia a última decisão.\"", caption: "Consulte o caso a qualquer momento. Voz ou texto." },
          { message: "Kit da audiência pronto. ⚠ Prazo TRT-2 em 3 dias. Minuta da réplica na sua caixa.", caption: "Receba briefings, minutas e alertas — onde você estiver." },
        ],
      },
      {
        tag: "↔ ASSISTENTE EM SEU NOME",
        subtitle: "Lexiun fala com as pessoas que você prefere não cobrar pessoalmente.",
        items: [
          { message: "\"Olá, aqui é o Lexiun em nome do Dr. Silva. Pode confirmar sua disponibilidade para a próxima semana?\"", caption: "Contata clientes em seu nome — no seu tom, com sua autorização." },
          { message: "\"Dando continuidade ao pedido de documentos para o RH da Distribuidora — alguma novidade sobre a folha de pagamento?\"", caption: "Cobra RH, peritos, parte contrária — até obter resposta." },
          { message: "\"Audiência confirmada: qui 14h, Vara 18 TRT-2. Transporte às 13h da R. Boa Vista.\"", caption: "Confirma testemunhas para audiência — agenda, lembra, coordena." },
          { message: "🎤 [mensagem de voz: 0:42]", caption: "Áudio entra, áudio sai. Como um assistente humano de verdade — porque é assim que as conversas acontecem no Brasil." },
        ],
      },
    ],
    closing: "O mesmo caso. Cada mensagem registrada. WhatsApp é a camada de conveniência — o trabalho real acontece no workspace.",
  },
  brazil: {
    headline: "Pensado para o Brasil.",
    body: "CPC, CLT, CDC, CTN — não adaptações, mas fundações. LGPD desde o primeiro dia. Todos os dados no Brasil. Nativo no sistema jurídico brasileiro.",
  },
  architect: {
    line1: "Projetado por um arquiteto que pensa em código e em direito.",
    line2: "30+ anos de arquitetura de software. Profunda expertise jurídica prática. Uma mente. Uma visão.",
    line3: "Por isso Lexiun não parece IA acoplada ao trabalho jurídico — parece o trabalho jurídico reimaginado como sistema.",
  },
  close: {
    headline: "Programa de Acesso Antecipado",
    body: "Estamos construindo Lexiun com parceiros pioneiros. Se isso faz sentido para você, queremos ouvir.",
    cta: "Acesso Antecipado",
  },
  footer: {
    tagline: "O especialista jurídico de IA. Em cada caso.",
    location: "São Paulo, Brasil",
    contact: "Contato",
    legal: "Legal",
    privacy: "Política de Privacidade",
    terms: "Termos de Uso",
    copyright: "© 2026 Lexiun AI. Todos os direitos reservados.",
  },
};

const EN = {
  pageTitle: "Lexiun — The AI Legal Expert. On Every Case.",
  nav: {
    signIn: "Sign In",
  },
  hero: {
    headline: "Lexiun works for you.\nIt thinks, acts, delivers.",
    subline:
      "Lexiun becomes the dedicated expert on every case in your firm. Builds the case from raw materials. Knows every detail. Acts before you ask.\nReads, drafts, monitors, verifies, and answers — wherever you are.\nAlways on. Also on WhatsApp and Telegram.",
    sublineClose: "Not just an AI tool. An Expert. On every case.",
    cta: "Apply for Early Access",
    badge: "Private preview · Q3 2026 launch",
  },
  problem: {
    quote: "AI can invent jurisprudence that looks real.",
    body: "The biggest risk in legal AI isn't getting a wrong answer — it's getting a convincing wrong answer. Lexiun eliminates that risk. An army of specialized agents, powered by multiple frontier AI models, cross-inspect and cross-analyse each other's work. Every output is verified and grounded in the case's own sources. No fabricated jurisprudence. No invented case law. Every citation is real and traceable.",
  },
  answer: {
    headline: "More than answering questions. Lexiun becomes the expert on each of your cases.",
    body: "From the first draft to the latest decision, Lexiun builds permanent knowledge over the case. It reads everything. It forgets nothing. It catches what humans miss. And it gets sharper with every case.",
    body2: "Lexiun is the 24/7 always-on case expert — ready to answer any query with sharp precision, debate a last-minute idea, or take action from the lawyer's WhatsApp voice message at midnight.",
  },
  capabilities: {
    label: "What your expert does",
    cards: [
      { title: "Total Recall", body: "No detail lost. No document overlooked. Complete case memory that never degrades, never resets, never forgets." },
      { title: "Risk Radar", body: "Citation defects. Missed deadlines. Standing issues. Prescription risks. Detected automatically, flagged instantly." },
      { title: "Full Petitions, Not Fragments", body: "From legal strategy to formatted petition with cited jurisprudence, structured annexes, and full appendices. In minutes." },
      { title: "Rewrites in Seconds", body: "Highlight a paragraph. Describe what you want. The entire document adjusts — arguments, citations, structure. Seconds." },
      { title: "Every Citation Is Real", body: "Every citation, every reference — verified and grounded in real sources. No invented jurisprudence. Ever." },
      { title: "Works While You Sleep", body: "While you sleep, Lexiun monitors deadlines, flags new risks, and prepares materials. Ready when you are." },
      { title: "Your Thinking Partner", body: "Ask Lexiun to poke holes in your argument. It will. And then help you build a better one." },
      { title: "From Chaos to Case", body: "WhatsApp audios, emails, client conversations, documents, photos — dump everything. Lexiun builds the case from raw chaos. Minutes, not months." },
    ],
  },
  workflow: {
    headline: "Stop drafting. Focus on deciding.",
    steps: ["Describe\nwhat you need\nin plain\nlanguage", "Lexiun\ndrafts, formats,\ncites, and\nannexes", "Review\n& approve\nor refine with\none sentence"],
    detail: "Express your intent in plain language — by chat, voice, or WhatsApp. Lexiun does the rest.",
    closing: "Strategy is the lawyer's job. Everything else is Lexiun's.",
  },
  chaosToCase: {
    headline: "Dump everything. Lexiun builds the case.",
    body: "Lawyers don't receive information in neat folders. They get voice messages at midnight, email chains with 47 forwards, scanned documents in random order, and handwritten notes on napkins. Lexiun takes all of it, makes sense of everything, and builds the case from the ground up.",
    inputs: ["Voice messages", "Emails", "Documents", "Chat logs", "Photos", "Court filings"],
    outputs: ["Facts", "Parties", "Legislation", "Analysis", "Case ready"],
  },
  midCta: {
    line: "Enough reading. Let's talk.",
    cta: "Apply for Early Access",
  },
  firm: {
    headline: "A specialist on every case. Across the whole firm.",
    orgLabel: "ORGANIZATION",
    nodeLabels: {
      lawyerA: "Lawyer A + AI",
      lawyerB: "Lawyer B + AI",
      clientPortal: "Client Portal",
    },
    points: [
      "Every lawyer gets a dedicated AI expert — across all their cases",
      "Firm-wide quality standards, enforced automatically",
      "Clients can check their own case status — with configurable access",
      "Organization-level controls, per-case permissions",
    ],
  },
  whatsapp: {
    label: "INSTANT MESSENGER · CONVENIENCE CHANNEL",
    headline: "Your assistant. On WhatsApp. Always.",
    subhead: "Reach your Lexiun agent anytime. Text, voice, documents. Let Lexiun talk to people on your behalf.",
    panels: [
      {
        tag: "↔ TALK TO YOUR ASSISTANT",
        subtitle: "Reach Lexiun from anywhere. Voice or text. Anytime.",
        items: [
          { message: "\"Draft a reply brief for Andrade v. Silva, lean on the statute of limitations.\"", caption: "Send voice instructions from anywhere. Lexiun delivers in minutes." },
          { message: "[forwarded: client voice note, scanned contract, photo of handwritten notes]", caption: "Forward anything. Lexiun captures and ingests it into the case." },
          { message: "\"What's the status of the Andrade matter? Read me the latest decision.\"", caption: "Pull case info on demand. Voice or text. Anytime." },
          { message: "Morning kit ready. ⚠ Filing deadline in 3 days. Reply brief draft is in your inbox.", caption: "Receive briefings, drafts, and alerts — wherever you are." },
        ],
      },
      {
        tag: "↔ ASSISTANT ON YOUR BEHALF",
        subtitle: "Lexiun reaches the people you'd rather not chase yourself.",
        items: [
          { message: "\"Hi, this is Lexiun on behalf of Dr. Silva. Could you confirm your availability for next week?\"", caption: "Reaches clients in your name — in your tone, with your authorization." },
          { message: "\"Following up on the document request to the company's HR department — any update on the payroll records?\"", caption: "Chases HR, expert witnesses, opposing counsel — until they respond." },
          { message: "\"Hearing confirmed: Thu 2 PM, Courtroom 18. Pickup at 1 PM from the office.\"", caption: "Confirms witnesses for the hearing — schedules, reminds, coordinates." },
          { message: "🎤 [voice message: 0:42]", caption: "Audio in, audio out. Just like a real assistant — because that's how real conversations happen." },
        ],
      },
    ],
    closing: "Same case file. Every message tracked. WhatsApp is the convenience layer — the real work happens in the workspace.",
  },
  brazil: {
    headline: "Launching in Brazil. Expanding globally.",
    body: "Lexiun debuts in Brazil — one of the world's most complex legal systems — as its first market. Built to master Brazilian procedural law from day one. Global expansion follows shortly.",
  },
  architect: {
    line1: "Designed by an architect who thinks in both code and law.",
    line2: "30+ years of software architecture. Deep, hands-on legal expertise. One mind. One vision.",
    line3: "That's why Lexiun doesn't feel like AI bolted onto legal work — it feels like legal work reimagined as a system.",
  },
  close: {
    headline: "Join the Early Access Program",
    body: "We're building Lexiun with early partners. If this resonates, we want to hear from you.",
    cta: "Apply for Early Access",
  },
  footer: {
    tagline: "The AI legal expert. On every case.",
    location: "São Paulo, Brazil",
    contact: "Contact",
    legal: "Legal",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    copyright: "© 2026 Lexiun AI. All rights reserved.",
  },
};

/* ─── ICONS ──────────────────────────────────────────────────────────────── */

function LinkedInIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

/* ─── INPUT/OUTPUT/ENTITY ICONS (for ChaosToCase + Firm sections) ─────── */

const iconStroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

/* Larger illustrated visuals for ChaosToCase inputs (40x40 viewbox) */

function VisualWaveform() {
  // Animated audio waveform — 11 vertical bars of varying heights, pulsing
  const bars = [10, 18, 26, 14, 22, 30, 16, 24, 12, 20, 8];
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, height: 36 }}>
      {bars.map((h, i) => (
        <div
          key={i}
          style={{
            width: 3,
            height: h,
            backgroundColor: copper,
            borderRadius: 2,
            transformOrigin: "center",
            animation: `wavepulse 1.4s ease-in-out ${i * 0.08}s infinite`,
            boxShadow: `0 0 6px rgba(212,160,82,0.4)`,
          }}
        />
      ))}
    </div>
  );
}

function VisualEnvelope() {
  return (
    <svg width="44" height="36" viewBox="0 0 44 36" fill="none" aria-hidden="true">
      <rect x="2" y="4" width="40" height="28" rx="2" stroke={copper} strokeWidth="1.6" />
      <path d="M2 6 L22 22 L42 6" stroke={copper} strokeWidth="1.6" strokeLinejoin="round" />
      <line x1="8" y1="20" x2="14" y2="20" stroke={copper} strokeWidth="1.2" opacity="0.5" />
      <line x1="30" y1="20" x2="36" y2="20" stroke={copper} strokeWidth="1.2" opacity="0.5" />
    </svg>
  );
}

function VisualDocStack() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      {/* Back doc */}
      <rect x="6" y="6" width="22" height="28" rx="1.5" stroke={copper} strokeWidth="1.4" opacity="0.4" />
      {/* Middle doc */}
      <rect x="9" y="9" width="22" height="28" rx="1.5" stroke={copper} strokeWidth="1.4" opacity="0.65" />
      {/* Front doc */}
      <rect x="12" y="12" width="22" height="28" rx="1.5" stroke={copper} strokeWidth="1.6" fill="rgba(212,160,82,0.05)" />
      <line x1="16" y1="20" x2="30" y2="20" stroke={copper} strokeWidth="1.2" />
      <line x1="16" y1="25" x2="30" y2="25" stroke={copper} strokeWidth="1.2" />
      <line x1="16" y1="30" x2="26" y2="30" stroke={copper} strokeWidth="1.2" />
    </svg>
  );
}

function VisualChatBubbles() {
  return (
    <svg width="44" height="36" viewBox="0 0 44 36" fill="none" aria-hidden="true">
      {/* Left bubble */}
      <path d="M4 6 H22 a3 3 0 0 1 3 3 V18 a3 3 0 0 1 -3 3 H10 L4 25 Z" stroke={copper} strokeWidth="1.5" strokeLinejoin="round" fill="rgba(212,160,82,0.06)" />
      {/* Right bubble */}
      <path d="M40 14 H22 a3 3 0 0 0 -3 3 V26 a3 3 0 0 0 3 3 H34 L40 33 Z" stroke={copper} strokeWidth="1.5" strokeLinejoin="round" fill="rgba(212,160,82,0.1)" opacity="0.85" />
    </svg>
  );
}

function VisualPhoto() {
  return (
    <svg width="40" height="36" viewBox="0 0 40 36" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="34" height="28" rx="2" stroke={copper} strokeWidth="1.6" fill="rgba(212,160,82,0.04)" />
      <circle cx="13" cy="14" r="2.5" stroke={copper} strokeWidth="1.4" />
      <path d="M3 28 L14 18 L22 24 L28 19 L37 28" stroke={copper} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function VisualCourtFile() {
  return (
    <svg width="42" height="36" viewBox="0 0 42 36" fill="none" aria-hidden="true">
      <path d="M2 7 a2 2 0 0 1 2 -2 H14 l3 3 H38 a2 2 0 0 1 2 2 V30 a2 2 0 0 1 -2 2 H4 a2 2 0 0 1 -2 -2 Z" stroke={copper} strokeWidth="1.6" fill="rgba(212,160,82,0.05)" />
      {/* Stamp/seal accent */}
      <circle cx="30" cy="22" r="4" stroke={copper} strokeWidth="1.4" opacity="0.7" />
      <line x1="27" y1="22" x2="33" y2="22" stroke={copper} strokeWidth="1.2" opacity="0.7" />
    </svg>
  );
}

const chaosInputVisuals = [VisualWaveform, VisualEnvelope, VisualDocStack, VisualChatBubbles, VisualPhoto, VisualCourtFile];

/* Output icons — slightly larger for visual weight */

function IconFact() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><circle cx="12" cy="12" r="9" /><polyline points="9 12 11 14 15 10" /></svg>); }
function IconParties() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5" /><path d="M14 20c0-2 2-3.5 4-3.5s4 1.5 4 3.5" /></svg>); }
function IconLegis() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><path d="M12 3v18" /><path d="M5 7h14" /><path d="M5 7 3 13a4 4 0 0 0 8 0z" /><path d="M19 7l-2 6a4 4 0 0 0 8 0z" transform="translate(-6 0)" /></svg>); }
function IconAnalysis() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><polyline points="3 17 9 11 13 15 21 7" /><polyline points="14 7 21 7 21 14" /></svg>); }
function IconCase() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>); }
function IconMic() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2" /><line x1="12" y1="19" x2="12" y2="22" /></svg>); }
function IconMail() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><polyline points="3 7 12 13 21 7" /></svg>); }
function IconChat() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><path d="M21 15a2 2 0 0 1 -2 2 H7 L3 21 V5 a2 2 0 0 1 2 -2 H19 a2 2 0 0 1 2 2 z" /></svg>); }
function IconDoc() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><path d="M14 2H6a2 2 0 0 0 -2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="14" y2="17" /></svg>); }

const chaosOutputIcons = [IconFact, IconParties, IconLegis, IconAnalysis, IconCase];

/* Workflow step illustrations (32x32) */

function IconDescribe() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      {/* Speech bubble with text lines + small mic accent */}
      <path d="M4 6 a2 2 0 0 1 2 -2 H22 a2 2 0 0 1 2 2 V18 a2 2 0 0 1 -2 2 H10 L4 25 Z" stroke={copper} strokeWidth="1.6" strokeLinejoin="round" fill="rgba(212,160,82,0.06)" />
      <line x1="8" y1="11" x2="20" y2="11" stroke={copper} strokeWidth="1.4" strokeLinecap="round" />
      <line x1="8" y1="15" x2="16" y2="15" stroke={copper} strokeWidth="1.4" strokeLinecap="round" />
      {/* Mic dot */}
      <circle cx="26" cy="22" r="3.5" stroke={copper} strokeWidth="1.4" fill="rgba(212,160,82,0.12)" />
      <line x1="26" y1="20" x2="26" y2="24" stroke={copper} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconDraft() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      {/* Document with sparkle accents */}
      <path d="M8 4 H20 L26 10 V26 a2 2 0 0 1 -2 2 H8 a2 2 0 0 1 -2 -2 V6 a2 2 0 0 1 2 -2 Z" stroke={copper} strokeWidth="1.6" strokeLinejoin="round" fill="rgba(212,160,82,0.06)" />
      <path d="M20 4 V10 H26" stroke={copper} strokeWidth="1.6" strokeLinejoin="round" />
      <line x1="10" y1="15" x2="22" y2="15" stroke={copper} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="10" y1="19" x2="22" y2="19" stroke={copper} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="10" y1="23" x2="18" y2="23" stroke={copper} strokeWidth="1.3" strokeLinecap="round" />
      {/* Sparkle */}
      <path d="M26 16 L27 18 L29 19 L27 20 L26 22 L25 20 L23 19 L25 18 Z" fill={copper} opacity="0.8" />
    </svg>
  );
}

function IconReview() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      {/* Document with checkmark seal */}
      <path d="M8 4 H22 a2 2 0 0 1 2 2 V26 a2 2 0 0 1 -2 2 H8 a2 2 0 0 1 -2 -2 V6 a2 2 0 0 1 2 -2 Z" stroke={copper} strokeWidth="1.6" strokeLinejoin="round" fill="rgba(212,160,82,0.06)" />
      <line x1="10" y1="10" x2="20" y2="10" stroke={copper} strokeWidth="1.3" strokeLinecap="round" opacity="0.6" />
      <line x1="10" y1="14" x2="18" y2="14" stroke={copper} strokeWidth="1.3" strokeLinecap="round" opacity="0.6" />
      {/* Big checkmark circle overlapping bottom-right */}
      <circle cx="22" cy="22" r="6" fill="rgba(212,160,82,0.18)" stroke={copper} strokeWidth="1.6" />
      <polyline points="19 22 21.5 24.5 25 20.5" stroke={copper} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

const workflowStepIcons = [IconDescribe, IconDraft, IconReview];

function IconBuilding() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><rect x="4" y="3" width="16" height="18" rx="1" /><line x1="9" y1="8" x2="9" y2="8" /><line x1="15" y1="8" x2="15" y2="8" /><line x1="9" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="9" y2="16" /><line x1="15" y1="16" x2="15" y2="16" /><line x1="10" y1="21" x2="14" y2="21" /></svg>); }
function IconLawyer() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><circle cx="12" cy="8" r="3.5" /><path d="M5 21c0-4 3-6.5 7-6.5s7 2.5 7 6.5" /></svg>); }
function IconClient() { return (<svg width="18" height="18" viewBox="0 0 24 24" {...iconStroke} aria-hidden="true"><circle cx="12" cy="9" r="3" /><path d="M5 21c0-4 3-6 7-6s7 2 7 6" /><path d="M19 4v4M17 6h4" /></svg>); }

/* ─── LOGO ───────────────────────────────────────────────────────────────── */

function Logo({ light = false, white = false, size = "md" }: { light?: boolean; white?: boolean; size?: "sm" | "md" }) {
  const wordmarkW = size === "sm" ? 96 : 120;
  const wordmarkH = size === "sm" ? 26 : 32;
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        src="/lexiun-wordmark.svg"
        alt="Lexiun"
        width={wordmarkW}
        height={wordmarkH}
        style={white ? { filter: "brightness(0) invert(1)" } : light ? { filter: "grayscale(1) brightness(0)" } : undefined}
      />
    </div>
  );
}

/* ─── NAV BAR ────────────────────────────────────────────────────────────── */

const NAV_LINKS = [
  { label: "Why Lexiun", labelPt: "Por que Lexiun", href: "#the-problem" },
  { label: "Capabilities", labelPt: "Capacidades", href: "#capabilities" },
  { label: "Messaging", labelPt: "WhatsApp", href: "#whatsapp" },
  { label: "Contact", labelPt: "Contato", href: "#contact" },
];

function NavBar({ lang, setLang }: { lang: "en" | "pt"; setLang: (l: "en" | "pt") => void }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const ctaLabel = lang === "pt" ? PT_BR.hero.cta : EN.hero.cta;

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 200,
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 clamp(20px, 5vw, 60px)",
        backgroundColor: scrolled ? "rgba(11,13,17,0.92)" : "rgba(11,13,17,0.6)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: scrolled ? "1px solid rgba(212,160,82,0.12)" : "1px solid transparent",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
      }}
    >
      <Logo white />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "clamp(20px, 3vw, 40px)",
        }}
        className="nav-links"
      >
        {NAV_LINKS.map(({ label, labelPt, href }) => (
          <a
            key={label}
            href={href}
            style={{
              color: "rgba(242,238,233,0.75)",
              fontSize: "0.875rem",
              fontWeight: 400,
              textDecoration: "none",
              letterSpacing: "0.01em",
              fontFamily: "'DM Sans', system-ui, sans-serif",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = "#F2EEE9";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color = "rgba(242,238,233,0.75)";
            }}
          >
            {lang === "pt" ? labelPt : label}
          </a>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        {/* Language toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
          {([
            { code: "en" as const, label: "EN", title: "English" },
            { code: "pt" as const, label: "PT", title: "Português (Brasil)" },
          ]).map(({ code, label, title }, i) => (
            <span key={code} style={{ display: "flex", alignItems: "center" }}>
              {i > 0 && (
                <span style={{ color: "rgba(242,238,233,0.2)", fontSize: "0.75rem", padding: "0 6px" }}>|</span>
              )}
              <button
                onClick={() => setLang(code)}
                title={title}
                style={{
                  background: "none",
                  border: "none",
                  cursor: lang === code ? "default" : "pointer",
                  padding: 0,
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontSize: "0.8125rem",
                  fontWeight: lang === code ? 500 : 400,
                  letterSpacing: "0.06em",
                  color: lang === code ? "#F2EEE9" : "rgba(242,238,233,0.4)",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => { if (lang !== code) (e.currentTarget as HTMLButtonElement).style.color = "rgba(242,238,233,0.75)"; }}
                onMouseLeave={(e) => { if (lang !== code) (e.currentTarget as HTMLButtonElement).style.color = "rgba(242,238,233,0.4)"; }}
              >
                {label}
              </button>
            </span>
          ))}
        </div>

        <a
          href={SIGN_IN_URL}
          className="nav-auth"
          style={{
            color: "rgba(242,238,233,0.75)",
            fontSize: "0.8125rem",
            fontWeight: 400,
            textDecoration: "none",
            letterSpacing: "0.02em",
            fontFamily: "'DM Sans', system-ui, sans-serif",
            whiteSpace: "nowrap",
            transition: "color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.color = "#F2EEE9";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.color = "rgba(242,238,233,0.75)";
          }}
        >
          {lang === "pt" ? PT_BR.nav.signIn : EN.nav.signIn}
        </a>

        <a
          href="/early-access"
          className="nav-cta"
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "8px 20px",
            background: "#F2EEE9",
            color: "#0B0D11",
            fontSize: "0.8125rem",
            fontWeight: 500,
            textDecoration: "none",
            letterSpacing: "0.02em",
            fontFamily: "'DM Sans', system-ui, sans-serif",
            borderRadius: "100px",
            whiteSpace: "nowrap",
            transition: "background 0.2s ease, box-shadow 0.2s ease",
          }}
          onMouseEnter={(e) => {
            const s = (e.currentTarget as HTMLAnchorElement).style;
            s.background = "#ffffff";
            s.boxShadow = "0 0 20px rgba(212,160,82,0.15)";
          }}
          onMouseLeave={(e) => {
            const s = (e.currentTarget as HTMLAnchorElement).style;
            s.background = "#F2EEE9";
            s.boxShadow = "none";
          }}
        >
          {ctaLabel}
        </a>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .nav-links { display: none !important; }
          .nav-cta { display: none !important; }
        }
      `}</style>
    </nav>
  );
}

/* ─── REVEAL HOOK ────────────────────────────────────────────────────────── */

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const targets = el.querySelectorAll("[data-reveal]");
    targets.forEach((t) => t.classList.add("pre-reveal"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("pre-reveal");
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

/* ─── SHARED STYLES ─────────────────────────────────────────────────────── */

const sectionPadding = "clamp(80px, 12vw, 160px) clamp(24px, 8vw, 120px)";
const maxW = 1100;
const serif = "'Playfair Display', Georgia, serif";
const sans = "'DM Sans', system-ui, sans-serif";
const copper = "#D4A052";
const textPrimary = "#F2EEE9";
const textMuted = "rgba(242,238,233,0.7)";
const bgDark = "#0B0D11";
const bgMid = "#111520";
const bgLight = "#151921";
const gradientText: React.CSSProperties = { fontWeight: 400, background: "linear-gradient(135deg, #F2EEE9 0%, #F2EEE9 60%, rgba(212,160,82,0.7) 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" };

const LEXIDE_URL = (import.meta.env.VITE_LEXIDE_URL ?? "http://localhost:5175").replace(/\/$/, "");
const SIGN_IN_URL = `${LEXIDE_URL}/sign-in`;

/* ─── MAIN PAGE ──────────────────────────────────────────────────────────── */

export default function LandingPage({ lang, setLang }: { lang: "en" | "pt"; setLang: (l: "en" | "pt") => void }) {
  const t = lang === "pt" ? PT_BR : EN;

  useEffect(() => {
    document.title = t.pageTitle;
  }, [t]);

  return (
    <main style={{ fontFamily: sans, overflowX: "hidden" }}>
      <NavBar lang={lang} setLang={setLang} />
      <HeroSection t={t.hero} />
      <AnswerSection t={t.answer} />
      <FirmSection t={t.firm} />
      <ChaosToCaseSection t={t.chaosToCase} />
      <WorkflowSection t={t.workflow} />
      <WhatsappSection t={t.whatsapp} />
      <ProblemSection t={t.problem} />
      <BrazilSection t={t.brazil} />
      <CapabilitiesSection t={t.capabilities} />
      {/* <MidCtaSection t={t.midCta} /> */}
      {/* <ArchitectSection t={t.architect} /> */}
      <ClosingSection t={t.close} />
      <SiteFooter t={t.footer} />
    </main>
  );
}

/* ─── §1 HERO ───────────────────────────────────────────────────────────── */

function HeroSection({ t }: { t: typeof EN.hero }) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const items = el.querySelectorAll("[data-hero-item]");
    items.forEach((item, i) => {
      const el2 = item as HTMLElement;
      el2.style.opacity = "0";
      el2.style.transform = "translateY(20px)";
      el2.style.transition = `opacity 0.7s ease ${i * 0.12}s, transform 0.7s ease ${i * 0.12}s`;
      requestAnimationFrame(() => {
        setTimeout(() => {
          el2.style.opacity = "1";
          el2.style.transform = "translateY(0)";
        }, 60);
      });
    });
  }, []);

  return (
    <section
      ref={ref}
      style={{
        minHeight: "100vh",
        backgroundColor: bgDark,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        textAlign: "left",
        padding: "100px clamp(24px, 8vw, 120px) 80px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, backgroundImage: "url('/hero-bg.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }} />
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(11,13,17,0.92) 0%, rgba(11,13,17,0.75) 40%, rgba(11,13,17,0.35) 70%, rgba(11,13,17,0.15) 100%)", pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 200, background: "linear-gradient(to bottom, rgba(11,13,17,0.7) 0%, transparent 100%)", pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 160, background: "linear-gradient(to top, rgba(11,13,17,0.7) 0%, transparent 100%)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 820, position: "relative", zIndex: 1 }}>
        <div data-hero-item style={{ display: "inline-flex", alignItems: "center", gap: 8, border: "1px solid rgba(212,160,82,0.35)", borderRadius: 40, padding: "6px 14px 6px 10px", marginBottom: 48, boxShadow: "0 0 20px rgba(212,160,82,0.06)", backgroundColor: "rgba(212,160,82,0.04)" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: copper, display: "inline-block", flexShrink: 0 }} />
          <span style={{ fontSize: "0.8rem", color: copper, letterSpacing: "0.03em", fontFamily: sans }}>{t.badge}</span>
        </div>

        <h1 data-hero-item style={{ fontFamily: serif, fontSize: "clamp(2.2rem, 4.4vw, 4.6rem)", fontWeight: 400, background: "linear-gradient(135deg, #F2EEE9 0%, #F2EEE9 60%, rgba(212,160,82,0.7) 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", lineHeight: 1.2, letterSpacing: "-0.015em", marginBottom: 28, fontVariantNumeric: "lining-nums", whiteSpace: "pre-line" }}>
          {t.headline}
        </h1>

        <div data-hero-item style={{ maxWidth: 620, marginBottom: 52 }}>
          <p style={{ fontSize: "clamp(1.05rem, 2vw, 1.25rem)", color: "rgba(242,238,233,0.82)", lineHeight: 1.7, marginBottom: 16, whiteSpace: "pre-line" }}>
            {t.subline}
          </p>
          <p style={{ fontSize: "clamp(1.05rem, 2vw, 1.25rem)", color: "rgba(242,238,233,0.95)", lineHeight: 1.7, fontWeight: 500 }}>
            {t.sublineClose}
          </p>
        </div>

        <a href="/early-access" data-hero-item style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#FFFFFF", color: bgDark, padding: "14px 36px", borderRadius: 40, fontSize: "0.95rem", fontWeight: 500, letterSpacing: "0.01em", textDecoration: "none", transition: "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease", fontFamily: sans }}
          onMouseEnter={(e) => { const s = (e.currentTarget as HTMLAnchorElement).style; s.backgroundColor = "rgba(255,255,255,0.92)"; s.transform = "translateY(-2px)"; s.boxShadow = "0 4px 30px rgba(212,160,82,0.25), 0 0 60px rgba(212,160,82,0.1)"; }}
          onMouseLeave={(e) => { const s = (e.currentTarget as HTMLAnchorElement).style; s.backgroundColor = "#FFFFFF"; s.transform = "translateY(0)"; s.boxShadow = "none"; }}
        >
          {t.cta}
        </a>
      </div>

      <div data-hero-item style={{ position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, opacity: 0.35, zIndex: 1 }}>
        <div style={{ width: 1, height: 48, background: "linear-gradient(to bottom, transparent, #F2EEE9)" }} />
      </div>
    </section>
  );
}

/* ─── §2 THE PROBLEM ────────────────────────────────────────────────────── */

function ProblemSection({ t }: { t: typeof EN.problem }) {
  const ref = useReveal(0.1);

  return (
    <section id="the-problem" ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgLight, padding: sectionPadding, position: "relative", overflow: "hidden" }}>

      <div style={{ maxWidth: maxW, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(40px, 6vw, 100px)", alignItems: "start" }} className="problem-grid">
          {/* Left: pull quote */}
          <div>
            <div style={{ width: 40, height: 3, backgroundColor: copper, marginBottom: 32 }} />
            <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(1.9rem, 3.2vw, 3rem)", ...gradientText, lineHeight: 1.18, letterSpacing: "-0.01em", fontStyle: "italic" }}>
              "{t.quote}"
            </h2>
          </div>

          {/* Right: body */}
          <div style={{ paddingTop: "clamp(0px, 2vw, 16px)" }}>
            <p data-reveal style={{ fontSize: "clamp(1rem, 1.4vw, 1.1rem)", color: "rgba(242,238,233,0.8)", lineHeight: 1.8 }}>
              {t.body}
            </p>
          </div>
        </div>
      </div>

      <style>{`@media (max-width: 720px) { .problem-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

/* ─── §3 THE ANSWER ─────────────────────────────────────────────────────── */

function AnswerSection({ t }: { t: typeof EN.answer }) {
  const ref = useReveal(0.1);

  return (
    <section ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgMid, padding: sectionPadding, position: "relative", overflow: "hidden", textAlign: "center" }}>
      {/* Strong copper atmospheric glow (moved from Problem section) */}
      <div aria-hidden="true" style={{ position: "absolute", top: "-30%", left: "-15%", width: "50%", height: "80%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.18) 0%, transparent 65%)", filter: "blur(80px)", pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", bottom: "-20%", right: "-10%", width: "40%", height: "60%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.12) 0%, transparent 65%)", filter: "blur(100px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 700, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(2rem, 3.5vw, 3.2rem)", fontWeight: 400, background: "linear-gradient(135deg, #F2EEE9 0%, #F2EEE9 60%, rgba(212,160,82,0.7) 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", lineHeight: 1.2, letterSpacing: "-0.015em", marginBottom: 28 }}>
          {t.headline}
        </h2>
        <p data-reveal style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)", color: textMuted, lineHeight: 1.8, marginBottom: 20 }}>
          {t.body}
        </p>
        <p data-reveal style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)", color: textMuted, lineHeight: 1.8 }}>
          {t.body2}
        </p>
      </div>
    </section>
  );
}

/* ─── §4 CAPABILITIES (8 cards) ─────────────────────────────────────────── */

function CapabilitiesSection({ t }: { t: typeof EN.capabilities }) {
  return (
    <section id="capabilities" style={{ backgroundColor: bgLight, padding: sectionPadding, position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{ position: "absolute", top: "20%", right: "-20%", width: "50%", height: "60%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.15) 0%, transparent 65%)", filter: "blur(100px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: maxW, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <p style={{ fontSize: "0.75rem", color: copper, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, marginBottom: 56, fontFamily: sans }}>
          {t.label}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, backgroundColor: "rgba(212,160,82,0.08)" }} className="capabilities-grid">
          {t.cards.map((card, i) => (
            <CapabilityCard key={card.title} card={card} index={i} />
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 820px) { .capabilities-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .capabilities-grid { grid-template-columns: 1fr !important; } }
        .cap-card:hover .cap-accent { transform: scaleX(1) !important; }
        .cap-card:hover { transform: translateY(-2px) !important; box-shadow: 0 8px 40px rgba(212,160,82,0.06) !important; border-color: rgba(212,160,82,0.15) !important; }
      `}</style>
    </section>
  );
}

function CapabilityCard({ card, index }: { card: { title: string; body: string }; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const num = String(index + 1).padStart(2, "0");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(16px)";
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              el.style.transition = "opacity 0.6s ease, transform 0.6s ease";
              el.style.opacity = "1";
              el.style.transform = "translateY(0)";
            }, index * 60);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div ref={ref} style={{ background: "linear-gradient(160deg, rgba(212,160,82,0.04) 0%, #181D28 40%, #181D28 100%)", padding: "clamp(28px, 3.5vw, 40px) clamp(24px, 3.5vw, 36px)", position: "relative", overflow: "hidden", cursor: "default", border: "1px solid rgba(212,160,82,0.0)", transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.3s ease" }} className="cap-card">
      <span style={{ display: "block", fontFamily: serif, fontSize: "2.8rem", fontWeight: 400, color: "rgba(212,160,82,0.18)", textShadow: "0 0 40px rgba(212,160,82,0.1)", lineHeight: 1, marginBottom: 16, letterSpacing: "-0.02em", userSelect: "none" }}>{num}</span>
      <h3 style={{ fontFamily: serif, fontSize: "1.35rem", ...gradientText, marginBottom: 10, letterSpacing: "-0.01em" }}>{card.title}</h3>
      <p style={{ fontSize: "0.9rem", color: "rgba(242,238,233,0.65)", lineHeight: 1.65 }}>{card.body}</p>

      <div className="cap-accent" style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, backgroundColor: copper, transform: "scaleX(0)", transformOrigin: "left", transition: "transform 0.3s ease" }} />
    </div>
  );
}

/* ─── §5 WORKFLOW ───────────────────────────────────────────────────────── */

function WorkflowSection({ t }: { t: typeof EN.workflow }) {
  const ref = useReveal(0.1);

  return (
    <section ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgLight, padding: sectionPadding, position: "relative", overflow: "hidden" }}>
      {/* Subtle copper glow */}
      <div aria-hidden="true" style={{ position: "absolute", top: "20%", right: "-15%", width: "45%", height: "60%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.1) 0%, transparent 65%)", filter: "blur(110px)", pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", bottom: "10%", left: "-10%", width: "35%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.08) 0%, transparent 65%)", filter: "blur(100px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: maxW, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(2rem, 3.5vw, 3.2rem)", ...gradientText, lineHeight: 1.15, letterSpacing: "-0.015em", marginBottom: 64, textAlign: "center" }}>
          {t.headline}
        </h2>

        {/* Flow steps */}
        <div data-reveal style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr auto 1fr", alignItems: "stretch", gap: "clamp(8px, 1.5vw, 20px)", marginBottom: 56, position: "relative" }} className="workflow-grid">
          {t.steps.map((step, i) => {
            const Icon = workflowStepIcons[i] ?? IconDescribe;
            const num = String(i + 1).padStart(2, "0");
            return (
              <>
                <div key={`step-${i}`} className="workflow-card" style={{
                  background: "linear-gradient(160deg, rgba(212,160,82,0.06) 0%, #181D28 50%, #181D28 100%)",
                  border: "1px solid rgba(212,160,82,0.18)",
                  borderRadius: 12,
                  padding: "clamp(24px, 2.8vw, 36px) clamp(16px, 2vw, 28px)",
                  textAlign: "center",
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 14,
                }}>
                  {/* Step number */}
                  <span style={{
                    fontFamily: serif,
                    fontSize: "1.6rem",
                    fontWeight: 400,
                    color: "rgba(212,160,82,0.45)",
                    lineHeight: 1,
                    letterSpacing: "0.02em",
                    userSelect: "none",
                  }}>
                    {num}
                  </span>

                  {/* Icon in a circle */}
                  <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "rgba(212,160,82,0.08)",
                    border: "1px solid rgba(212,160,82,0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 24px rgba(212,160,82,0.12)",
                  }}>
                    <Icon />
                  </div>

                  {/* Step text */}
                  <p style={{
                    fontSize: "0.92rem",
                    color: textPrimary,
                    lineHeight: 1.55,
                    whiteSpace: "pre-line",
                    fontWeight: 500,
                    fontFamily: sans,
                    margin: 0,
                  }}>
                    {step}
                  </p>

                  {/* Bottom accent line on hover */}
                  <div className="workflow-accent" style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: copper,
                    transform: "scaleX(0)",
                    transformOrigin: "left",
                    transition: "transform 0.4s ease",
                  }} />
                </div>

                {/* Arrow between cards */}
                {i < t.steps.length - 1 && (
                  <div key={`arrow-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "center" }} className="workflow-arrow-cell">
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: copper, boxShadow: `0 0 10px ${copper}`, animation: `flowdot 1.6s ease-in-out ${i * 0.2}s infinite` }} />
                      <div style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: copper, boxShadow: `0 0 10px ${copper}`, animation: `flowdot 1.6s ease-in-out ${i * 0.2 + 0.2}s infinite` }} />
                      <span style={{ color: copper, fontSize: "1.4rem", fontWeight: 300, lineHeight: 1, marginLeft: 2, textShadow: `0 0 12px rgba(212,160,82,0.4)` }}>→</span>
                    </div>
                  </div>
                )}
              </>
            );
          })}
        </div>

        <p data-reveal style={{ textAlign: "center", fontSize: "clamp(0.95rem, 1.3vw, 1.05rem)", color: textMuted, lineHeight: 1.7, marginBottom: 20, maxWidth: 720, margin: "0 auto 20px" }}>
          {t.detail}
        </p>
        <p data-reveal style={{ textAlign: "center", fontFamily: serif, fontSize: "clamp(1.1rem, 1.6vw, 1.35rem)", color: "rgba(242,238,233,0.88)", fontStyle: "italic", lineHeight: 1.6, maxWidth: 720, margin: "0 auto" }}>
          {t.closing}
        </p>
      </div>

      <style>{`
        .workflow-card:hover { transform: translateY(-3px); border-color: rgba(212,160,82,0.32) !important; box-shadow: 0 10px 40px rgba(212,160,82,0.1) !important; }
        .workflow-card:hover .workflow-accent { transform: scaleX(1) !important; }
        @media (max-width: 820px) {
          .workflow-grid { grid-template-columns: 1fr !important; }
          .workflow-arrow-cell { transform: rotate(90deg); padding: 8px 0; }
        }
      `}</style>
    </section>
  );
}

/* ─── §5b FROM CHAOS TO CASE ★ ──────────────────────────────────────────── */

function ChaosToCaseSection({ t }: { t: typeof EN.chaosToCase }) {
  const ref = useReveal(0.1);

  return (
    <section ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgDark, padding: sectionPadding, position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{ position: "absolute", top: "30%", left: "-10%", width: "40%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.12) 0%, transparent 60%)", filter: "blur(100px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: maxW, margin: "0 auto", position: "relative", zIndex: 1 }}>
        {/* <p style={{ fontSize: "0.75rem", color: copper, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, marginBottom: 20, fontFamily: sans }} data-reveal>
          ★
        </p> */}

        <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(2rem, 3.5vw, 3.2rem)", ...gradientText, lineHeight: 1.15, letterSpacing: "-0.015em", marginBottom: 48 }}>
          {t.headline}
        </h2>

        {/* Visual: raw input → structured output */}
        <div data-reveal style={{ display: "grid", gridTemplateColumns: "1.4fr auto 1fr", gap: "clamp(24px, 4vw, 56px)", alignItems: "center", marginBottom: 56 }} className="chaos-grid">
          {/* Raw inputs — 3x2 grid of large illustrated cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "clamp(12px, 1.5vw, 18px)" }} className="chaos-inputs">
            {t.inputs.map((input, i) => {
              const Visual = chaosInputVisuals[i] ?? VisualDocStack;
              return (
                <div
                  key={input}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    padding: "clamp(16px, 2vw, 22px) clamp(8px, 1.5vw, 14px)",
                    backgroundColor: "rgba(212,160,82,0.05)",
                    border: "1px solid rgba(212,160,82,0.18)",
                    borderRadius: 10,
                    minHeight: 110,
                    transition: "transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
                  }}
                  className="chaos-input-card"
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 40 }}>
                    <Visual />
                  </div>
                  <span style={{ fontSize: "0.78rem", color: "rgba(242,238,233,0.82)", fontFamily: sans, textAlign: "center", lineHeight: 1.3, letterSpacing: "0.01em" }}>
                    {input}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Animated flow arrow with glow */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: copper, boxShadow: `0 0 12px ${copper}`, animation: "flowdot 1.6s ease-in-out 0s infinite" }} />
              <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: copper, boxShadow: `0 0 12px ${copper}`, animation: "flowdot 1.6s ease-in-out 0.2s infinite" }} />
              <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: copper, boxShadow: `0 0 12px ${copper}`, animation: "flowdot 1.6s ease-in-out 0.4s infinite" }} />
            </div>
            <span style={{ color: copper, fontSize: "2.4rem", fontWeight: 300, lineHeight: 1, textShadow: `0 0 20px rgba(212,160,82,0.5)` }}>→</span>
          </div>

          {/* Structured outputs — taller bars with stronger visual weight */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {t.outputs.map((output, i) => {
              const Icon = chaosOutputIcons[i] ?? IconFact;
              return (
                <div
                  key={output}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 18px",
                    backgroundColor: "rgba(212,160,82,0.06)",
                    border: "1px solid rgba(212,160,82,0.18)",
                    borderRadius: 6,
                    transition: "transform 0.25s ease, border-color 0.25s ease",
                  }}
                  className="chaos-output-card"
                >
                  <span style={{ color: copper, display: "inline-flex", flexShrink: 0 }}><Icon /></span>
                  <span style={{ fontSize: "0.92rem", color: textPrimary, fontFamily: sans, fontWeight: 500 }}>{output}</span>
                </div>
              );
            })}
          </div>
        </div>

        <p data-reveal style={{ fontSize: "clamp(1rem, 1.4vw, 1.1rem)", color: textMuted, lineHeight: 1.8, maxWidth: 700 }}>
          {t.body}
        </p>
      </div>

      <style>{`
        @keyframes wavepulse {
          0%, 100% { transform: scaleY(0.5); }
          50% { transform: scaleY(1.3); }
        }
        @keyframes flowdot {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        .chaos-input-card:hover { transform: translateY(-2px); border-color: rgba(212,160,82,0.35) !important; box-shadow: 0 8px 30px rgba(212,160,82,0.1); }
        .chaos-output-card:hover { transform: translateX(2px); border-color: rgba(212,160,82,0.32) !important; }
        @media (max-width: 820px) { .chaos-grid { grid-template-columns: 1fr !important; text-align: center; } }
        @media (max-width: 480px) { .chaos-inputs { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </section>
  );
}

/* ─── §5d MID-PAGE CTA ──────────────────────────────────────────────────── */

function MidCtaSection({ t }: { t: typeof EN.midCta }) {
  return (
    <section style={{ backgroundColor: bgDark, padding: "clamp(48px, 6vw, 80px) 24px", textAlign: "center", position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(212,160,82,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <p style={{ fontSize: "1rem", color: textMuted, marginBottom: 24, fontFamily: sans }}>{t.line}</p>
        <a href="/early-access" style={{ display: "inline-flex", alignItems: "center", backgroundColor: copper, color: bgDark, padding: "14px 36px", borderRadius: 3, fontSize: "0.95rem", fontWeight: 600, letterSpacing: "0.02em", textDecoration: "none", transition: "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease", boxShadow: "0 0 30px rgba(212,160,82,0.15)", fontFamily: sans }}
          onMouseEnter={(e) => { const s = (e.currentTarget as HTMLAnchorElement).style; s.backgroundColor = "#E0B468"; s.transform = "translateY(-2px)"; s.boxShadow = "0 4px 40px rgba(212,160,82,0.35)"; }}
          onMouseLeave={(e) => { const s = (e.currentTarget as HTMLAnchorElement).style; s.backgroundColor = copper; s.transform = "translateY(0)"; s.boxShadow = "0 0 30px rgba(212,160,82,0.15)"; }}
        >
          {t.cta}
        </a>
      </div>
    </section>
  );
}

/* ─── §6 YOUR FIRM, ELEVATED ────────────────────────────────────────────── */

function FirmSection({ t }: { t: typeof EN.firm }) {
  const ref = useReveal(0.1);

  return (
    <section ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgLight, padding: sectionPadding, position: "relative", overflow: "hidden" }}>
      <div style={{ maxWidth: maxW, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(40px, 6vw, 80px)", alignItems: "center" }} className="firm-grid">
          {/* Left: hierarchy visual */}
          <div data-reveal style={{ padding: "clamp(28px, 3vw, 44px)", backgroundColor: "rgba(212,160,82,0.03)", border: "1px solid rgba(212,160,82,0.12)", borderRadius: 8, position: "relative" }}>
            {/* Organization node */}
            <div style={{ textAlign: "center", marginBottom: 36, position: "relative" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, backgroundColor: "rgba(212,160,82,0.1)", border: "1px solid rgba(212,160,82,0.28)", borderRadius: 8, padding: "12px 22px", boxShadow: "0 0 20px rgba(212,160,82,0.08)" }}>
                <span style={{ color: copper, display: "inline-flex" }}><IconBuilding /></span>
                <span style={{ fontSize: "0.85rem", color: copper, fontWeight: 600, fontFamily: sans, letterSpacing: "0.05em" }}>{t.orgLabel}</span>
              </div>
              {/* Connector lines */}
              <svg width="100%" height="36" style={{ position: "absolute", top: "100%", left: 0, pointerEvents: "none" }} aria-hidden="true">
                <line x1="50%" y1="0" x2="50%" y2="14" stroke="rgba(212,160,82,0.3)" strokeWidth="1" />
                <line x1="16%" y1="14" x2="84%" y2="14" stroke="rgba(212,160,82,0.3)" strokeWidth="1" />
                <line x1="16%" y1="14" x2="16%" y2="32" stroke="rgba(212,160,82,0.3)" strokeWidth="1" />
                <line x1="50%" y1="14" x2="50%" y2="32" stroke="rgba(212,160,82,0.3)" strokeWidth="1" />
                <line x1="84%" y1="14" x2="84%" y2="32" stroke="rgba(212,160,82,0.3)" strokeWidth="1" />
              </svg>
            </div>

            {/* Child nodes */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              {[
                { label: t.nodeLabels.lawyerA, Icon: IconLawyer },
                { label: t.nodeLabels.lawyerB, Icon: IconLawyer },
                { label: t.nodeLabels.clientPortal, Icon: IconClient },
              ].map(({ label, Icon }) => (
                <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, backgroundColor: "rgba(242,238,233,0.04)", border: "1px solid rgba(242,238,233,0.1)", borderRadius: 6, padding: "14px 8px", textAlign: "center" }}>
                  <span style={{ color: copper, display: "inline-flex" }}><Icon /></span>
                  <span style={{ fontSize: "0.75rem", color: "rgba(242,238,233,0.7)", fontFamily: sans, lineHeight: 1.3 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: text */}
          <div>
            <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(1.8rem, 3vw, 2.6rem)", ...gradientText, lineHeight: 1.2, letterSpacing: "-0.015em", marginBottom: 28 }}>
              {t.headline}
            </h2>
            <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 16 }}>
              {t.points.map((point) => (
                <li key={point} data-reveal style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <span style={{ color: copper, flexShrink: 0, marginTop: 4, fontSize: "0.75rem" }}>●</span>
                  <span style={{ fontSize: "0.95rem", color: textMuted, lineHeight: 1.6 }}>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <style>{`@media (max-width: 720px) { .firm-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

/* ─── §7 WHATSAPP — Your assistant on WhatsApp. Always. ─────────────────── */

function WhatsappSection({ t }: { t: typeof EN.whatsapp }) {
  const ref = useReveal(0.1);

  return (
    <section id="whatsapp" ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgMid, padding: sectionPadding, position: "relative", overflow: "hidden" }}>
      {/* Atmospheric glows */}
      <div aria-hidden="true" style={{ position: "absolute", top: "20%", right: "-15%", width: "45%", height: "60%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.12) 0%, transparent 65%)", filter: "blur(100px)", pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", bottom: "10%", left: "-10%", width: "35%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(37,211,102,0.06) 0%, transparent 65%)", filter: "blur(100px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: maxW, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(2rem, 3.5vw, 3.2rem)", ...gradientText, lineHeight: 1.15, letterSpacing: "-0.015em", marginBottom: 20, textAlign: "center" }}>
          {t.headline}
        </h2>

        <p data-reveal style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)", color: textMuted, lineHeight: 1.7, maxWidth: 720, margin: "0 auto 64px", textAlign: "center" }}>
          {t.subhead}
        </p>

        {/* Two side-by-side panels */}
        <div data-reveal style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(20px, 3vw, 36px)", marginBottom: 56 }} className="whatsapp-panels">
          {t.panels.map((panel, panelIdx) => (
            <WhatsappPanel key={panelIdx} panel={panel} index={panelIdx} />
          ))}
        </div>

        <p data-reveal style={{ fontFamily: serif, fontSize: "clamp(1.05rem, 1.4vw, 1.2rem)", color: "rgba(242,238,233,0.85)", fontStyle: "italic", lineHeight: 1.7, maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          {t.closing}
        </p>
      </div>

      <style>{`
        @media (max-width: 820px) { .whatsapp-panels { grid-template-columns: 1fr !important; } }
        .wa-panel:hover { border-color: rgba(212,160,82,0.32) !important; box-shadow: 0 12px 50px rgba(212,160,82,0.08) !important; }
        .wa-item:hover .wa-item-icon { border-color: rgba(212,160,82,0.4) !important; box-shadow: 0 0 16px rgba(212,160,82,0.18) !important; }
      `}</style>
    </section>
  );
}

function WhatsappPanel({ panel, index }: { panel: typeof EN.whatsapp.panels[number]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  // Panel A icons: input → assistant
  // Panel B icons: assistant → outward
  const panelIcons = index === 0
    ? [IconMic, IconMail, IconChat, IconDoc]
    : [IconClient, IconParties, IconCase, IconMic];

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(16px)";
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              el.style.transition = "opacity 0.6s ease, transform 0.6s ease";
              el.style.opacity = "1";
              el.style.transform = "translateY(0)";
            }, index * 120);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      className="wa-panel"
      style={{
        backgroundColor: "rgba(212,160,82,0.04)",
        border: "1px solid rgba(212,160,82,0.18)",
        borderRadius: 14,
        padding: "clamp(28px, 3vw, 40px) clamp(24px, 2.5vw, 36px)",
        position: "relative",
        overflow: "hidden",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease",
      }}
    >
      {/* Top accent line */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(${index === 0 ? "90deg" : "270deg"}, ${copper} 0%, rgba(212,160,82,0.2) 100%)` }} />

      {/* Panel header */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: "0.72rem", color: copper, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10, fontFamily: sans }}>
          {panel.tag}
        </p>
        <p style={{ fontFamily: serif, fontSize: "clamp(1rem, 1.35vw, 1.15rem)", color: "rgba(242,238,233,0.82)", fontStyle: "italic", lineHeight: 1.5 }}>
          {panel.subtitle}
        </p>
      </div>

      {/* Items list */}
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 18 }}>
        {panel.items.map((item, i) => {
          const Icon = panelIcons[i] ?? IconChat;
          return (
            <li key={i} className="wa-item" style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div className="wa-item-icon" style={{
                flexShrink: 0,
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "rgba(212,160,82,0.08)",
                border: "1px solid rgba(212,160,82,0.22)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: copper,
                transition: "border-color 0.25s ease, box-shadow 0.25s ease",
              }}>
                <Icon />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: serif, fontSize: "0.98rem", color: "rgba(242,238,233,0.92)", fontStyle: "italic", lineHeight: 1.5, marginBottom: 6, letterSpacing: "-0.003em" }}>
                  {item.message}
                </p>
                <p style={{ fontSize: "0.78rem", color: "rgba(242,238,233,0.55)", lineHeight: 1.5, fontFamily: sans }}>
                  {item.caption}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ─── §8 BUILT FOR BRAZIL ──────────────────────────────────────────────── */

function BrazilSection({ t }: { t: typeof EN.brazil }) {
  const ref = useReveal(0.1);

  return (
    <section ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgMid, padding: "clamp(56px, 8vw, 100px) clamp(24px, 8vw, 120px)", position: "relative", overflow: "hidden", textAlign: "center", borderTop: "1px solid rgba(212,160,82,0.1)", borderBottom: "1px solid rgba(212,160,82,0.1)" }}>
      <div style={{ maxWidth: 700, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(1.6rem, 2.8vw, 2.4rem)", ...gradientText, lineHeight: 1.2, letterSpacing: "-0.01em", marginBottom: 16 }}>
          {t.headline}
        </h2>
        <p data-reveal style={{ fontSize: "clamp(0.95rem, 1.3vw, 1.05rem)", color: textMuted, lineHeight: 1.7 }}>
          {t.body}
        </p>
      </div>
    </section>
  );
}

/* ─── §9 THE ARCHITECT ──────────────────────────────────────────────────── */

function ArchitectSection({ t }: { t: typeof EN.architect }) {
  const ref = useReveal(0.1);

  return (
    <section id="architect" ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgLight, padding: sectionPadding, overflow: "hidden", position: "relative" }}>
      <div aria-hidden="true" style={{ position: "absolute", bottom: "-20%", left: "30%", width: "40%", height: "50%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.18) 0%, transparent 65%)", filter: "blur(80px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: maxW, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(40px, 6vw, 80px)", alignItems: "center", position: "relative", zIndex: 1 }} className="architect-grid">
        <div data-reveal style={{ position: "relative", borderRadius: 6, overflow: "hidden", aspectRatio: "16/10", border: "1px solid rgba(212,160,82,0.1)", boxShadow: "0 8px 60px rgba(0,0,0,0.4)" }}>
          <img src="/architect-bg.png" alt="Law books and architectural documents" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.2) 0%, transparent 40%)", pointerEvents: "none" }} />
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <p style={{ fontSize: "0.75rem", color: copper, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, marginBottom: 40, fontFamily: sans }}>The Architect</p>
          <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", ...gradientText, lineHeight: 1.2, marginBottom: 32, letterSpacing: "-0.015em" }}>{t.line1}</h2>
          <p data-reveal style={{ fontFamily: serif, fontSize: "clamp(1.05rem, 1.6vw, 1.25rem)", fontStyle: "italic", color: "rgba(242,238,233,0.75)", lineHeight: 1.7, marginBottom: 28, letterSpacing: "-0.005em" }}>{t.line2}</p>
          <div data-reveal style={{ width: 32, height: 1, backgroundColor: copper, marginBottom: 28, opacity: 0.6 }} />
          <p data-reveal style={{ fontSize: "clamp(0.95rem, 1.3vw, 1.05rem)", color: textMuted, lineHeight: 1.8, fontStyle: "italic" }}>{t.line3}</p>
        </div>
      </div>

      <style>{`@media (max-width: 720px) { .architect-grid { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

/* ─── §10 CLOSING CTA ───────────────────────────────────────────────────── */

function ClosingSection({ t }: { t: typeof EN.close }) {
  const ref = useReveal(0.08);

  return (
    <section id="contact" ref={ref as React.RefObject<HTMLElement>} style={{ backgroundColor: bgDark, padding: "clamp(100px, 14vw, 180px) 24px clamp(64px, 8vw, 100px)", textAlign: "center", position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 70% 55% at 50% 55%, rgba(212,160,82,0.08) 0%, rgba(212,160,82,0.03) 50%, transparent 75%)", pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", top: "-15%", left: "-10%", width: "40%", height: "40%", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.04) 0%, transparent 60%)", filter: "blur(80px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 600, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div data-reveal style={{ display: "flex", justifyContent: "center", marginBottom: 56 }}><Logo /></div>
        <h2 data-reveal style={{ fontFamily: serif, fontSize: "clamp(2.5rem, 5.5vw, 5rem)", ...gradientText, lineHeight: 1.2, letterSpacing: "-0.02em", marginBottom: 20 }}>{t.headline}</h2>
        <p data-reveal style={{ fontSize: "1rem", color: textMuted, marginBottom: 44, lineHeight: 1.6 }}>{t.body}</p>
        <a href="/early-access" data-reveal style={{ display: "inline-flex", alignItems: "center", backgroundColor: copper, color: bgDark, padding: "16px 44px", borderRadius: 3, fontSize: "1rem", fontWeight: 600, letterSpacing: "0.02em", textDecoration: "none", transition: "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease", boxShadow: "0 0 30px rgba(212,160,82,0.15)", marginBottom: 48, fontFamily: sans }}
          onMouseEnter={(e) => { const s = (e.currentTarget as HTMLAnchorElement).style; s.backgroundColor = "#E0B468"; s.transform = "translateY(-2px)"; s.boxShadow = "0 4px 40px rgba(212,160,82,0.35), 0 0 80px rgba(212,160,82,0.12)"; }}
          onMouseLeave={(e) => { const s = (e.currentTarget as HTMLAnchorElement).style; s.backgroundColor = copper; s.transform = "translateY(0)"; s.boxShadow = "0 0 30px rgba(212,160,82,0.15)"; }}
        >{t.cta}</a>
      </div>
    </section>
  );
}

/* ─── FOOTER ─────────────────────────────────────────────────────────────── */

function SiteFooter({ t }: { t: typeof EN.footer }) {
  const linkStyle: React.CSSProperties = {
    color: "#9CA3AF",
    textDecoration: "none",
    fontSize: "0.9rem",
    transition: "color 0.2s",
    display: "flex",
    alignItems: "center",
    gap: 10,
  };

  return (
    <footer style={{ backgroundColor: bgDark, borderTop: "1px solid rgba(212,160,82,0.18)", padding: "72px clamp(24px, 5vw, 60px) 48px", fontFamily: sans, position: "relative", overflow: "hidden" }}>
      <div aria-hidden="true" style={{ position: "absolute", top: "10%", right: "-5%", width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.12) 0%, transparent 65%)", filter: "blur(80px)", pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", bottom: "-10%", left: "10%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,82,0.08) 0%, transparent 65%)", filter: "blur(80px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: maxW, margin: "0 auto", display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: "clamp(40px, 6vw, 80px)", position: "relative", zIndex: 1 }} className="footer-grid">
        <div>
          <img src="/lexiun-wordmark.svg" alt="Lexiun" style={{ height: "1.75rem", width: "auto", display: "block", marginBottom: 16 }} />
          <p style={{ fontSize: "0.9rem", color: "#9CA3AF", lineHeight: 1.7, marginBottom: 8 }}>{t.tagline}</p>
          <p style={{ fontSize: "0.9rem", color: "#9CA3AF", lineHeight: 1.7, marginBottom: 20 }}>{t.location}</p>
        </div>

        <div>
          <h4 style={{ color: textPrimary, fontSize: "0.95rem", fontWeight: 600, marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={copper} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            {t.contact}
          </h4>
          <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            <li><a href="mailto:contact@lexiun.ai" style={linkStyle} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = textPrimary; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#9CA3AF"; }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>contact@lexiun.ai</a></li>
            {/* <li><a href="https://linkedin.com/company/lexiun" target="_blank" rel="noopener noreferrer" style={linkStyle} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = textPrimary; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#9CA3AF"; }}><LinkedInIcon />LinkedIn</a></li> */}
            {/* <li><a href="https://x.com/lexiun" target="_blank" rel="noopener noreferrer" style={linkStyle} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = textPrimary; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#9CA3AF"; }}><XIcon />@lexiun</a></li> */}
          </ul>
        </div>

        <div>
          <h4 style={{ color: textPrimary, fontSize: "0.95rem", fontWeight: 600, marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={copper} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            {t.legal}
          </h4>
          <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            <li><a href="#" style={{ ...linkStyle, gap: 0 }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = textPrimary; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#9CA3AF"; }}>{t.privacy}</a></li>
            <li><a href="#" style={{ ...linkStyle, gap: 0 }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = textPrimary; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#9CA3AF"; }}>{t.terms}</a></li>
          </ul>
        </div>
      </div>

      <div style={{ maxWidth: maxW, margin: "0 auto", marginTop: 56, paddingTop: 32, borderTop: "1px solid rgba(255,255,255,0.08)", textAlign: "center" }}>
        <p style={{ fontSize: "0.85rem", color: "#9CA3AF", letterSpacing: "0.02em" }}>{t.copyright}</p>
      </div>

      <style>{`@media (max-width: 640px) { .footer-grid { grid-template-columns: 1fr !important; } }`}</style>
    </footer>
  );
}
